// src/features/clients/pages/LatestRegistrationsPage.tsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/layout/Layout';
import {
  UserPlus, Calendar, Building, CalendarClock,
  Loader2, Users, DollarSign, Star // ✅ Agregados iconos para moneda
} from 'lucide-react';
import { useError } from '../../../context/ErrorContext';
import DateTimeDisplay from '../../../components/common/DateTimeDisplay';
import ClientContactInfo from '../../../components/common/ClientContactInfo';
import SearchInput from '../../../components/common/SearchInput';
import { clientsApi, UIClient } from '../../../api/clientsApi';
import useDateFormatting from '../../../hooks/useDateFormatting';
import PaginationControls from '../../../components/common/PaginationControls';

// --- INTERFACES AND CONSTANTS ---
interface ClientRegistration {
  id: string;
  visitorName: string | null;
  phoneNumber: string;
  countryCode: string;
  email: string | null;
  createdAt: Date;
  businessId: string;
  points: number;
  gender: string | null;
  registrationBranchId?: string;
  registrationBranchName?: string;
}

interface DailyRegistrations {
  date: string;
  dateObj: Date;
  registrations: ClientRegistration[];
  count: number;
}

const ITEMS_PER_PAGE = 20;

// --- HELPER FUNCTIONS (Optimizadas fuera del componente) ---

// Mapea los resultados de Elastic a tu interfaz
// base /contacts ya ordena por id DESC = registros más recientes primero.
// Gaps vs Firebase: género (base no captura) y sucursal de registro (no en el listado).
const mapUiToRegistration = (u: UIClient): ClientRegistration => ({
  id: u.id,
  visitorName: u.visitorName || null,
  phoneNumber: u.phoneNumber || '',
  countryCode: '+52',
  email: u.email || null,
  createdAt: u.createdAt ? new Date(u.createdAt) : new Date(),
  businessId: '',
  points: typeof u.points === 'number' ? u.points : 0,
  gender: u.gender || null,
  registrationBranchId: undefined,
  registrationBranchName: undefined,
});

// Formatea la fecha para usarla como clave de agrupación (YYYY-MM-DD)
const formatLocalDateKey = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Formatea la fecha para mostrarla al usuario (Ej: "Lunes, 25 de noviembre...")
const formatDateDisplay = (date: Date): string => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  };
  const formattedDate = date.toLocaleDateString('es-ES', options);
  return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
};

// --- COMPONENTE PRINCIPAL ---

const LatestRegistrationsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { addError, handleFirebaseError, clearErrors } = useError();
  const { timeZoneInfo } = useDateFormatting();

  // Referencias para control de paginación
  const isInitialMount = useRef(true);
  const requestedPageRef = useRef<number | null>(null);
  const contentTopRef = useRef<HTMLDivElement>(null);

  const [registrations, setRegistrations] = useState<ClientRegistration[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageChanging, setPageChanging] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalHits, setTotalHits] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // ✅ 1. DETECCIÓN DE MODO DE LEALTAD
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  // Check if the business has multiple branches
  const hasMultipleBranches = currentUser?.branches && currentUser.branches.filter(b => b.isActive !== false).length > 1;

  // Fetch registrations with pagination
  const fetchRegistrations = useCallback(async (
    page: number = 1,
    term: string = ''
  ) => {
    if (!currentUser?.businessId) {
      setRegistrations([]);
      setLoading(false);
      return;
    }

    // Guardar la página solicitada en la referencia
    requestedPageRef.current = page;
    
    try {
      // Si estamos cambiando de página (no es la carga inicial), usamos pageChanging en lugar de loading
      const isPageChange = currentPage !== page;
      
      if (isPageChange) {
        setPageChanging(true); // Estado de carga más ligero para cambios de página
      } else {
        setLoading(true); // Carga completa para búsquedas o carga inicial
      }
      
      if (clearErrors) clearErrors();

      // base /contacts: orden id DESC (más reciente primero) + búsqueda por q
      const result = await clientsApi.list({
        page,
        size: ITEMS_PER_PAGE,
        ...(term && { q: term }),
      });

      // Actualizar total de resultados
      const total = result.total || 0;
      setTotalHits(total);

      // Verificar si la página que vamos a establecer es la que solicitamos
      if (requestedPageRef.current === page) {
        setCurrentPage(page);
      }

      // Procesar resultados
      if (result.clients && result.clients.length > 0) {
        const registrationData = result.clients.map(mapUiToRegistration);

        // Usar un timeout mínimo para garantizar una mejor experiencia de usuario
        if (isPageChange) {
          setTimeout(() => {
            setRegistrations(registrationData);
            setPageChanging(false);
          }, 200); 
        } else {
          setRegistrations(registrationData);
        }
      } else {
        setRegistrations([]);
        if (isPageChange) {
          setPageChanging(false);
        }
      }
    } catch (error) {
      console.error("Error fetching latest clients:", error);
      if (handleFirebaseError) handleFirebaseError(error, 'fetch-registrations');
      if (addError) addError('No se pudieron cargar los últimos registros.', 'unknown', 'registrations-fetch');
      setPageChanging(false);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.businessId, clearErrors, handleFirebaseError, addError, currentPage]);

  // Handle search changes - reset to page 1
  const handleSearch = (term: string | null) => {
    const searchValue = term || '';
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to page 1 on new search
    fetchRegistrations(1, searchValue);
  };

  // Initial data load
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchRegistrations(1, searchTerm);
    }
  }, [fetchRegistrations, searchTerm]);

  // Pagination handlers
  const handleNextPage = useCallback(() => {
    if (!loading && !pageChanging && currentPage * ITEMS_PER_PAGE < totalHits) {
      const nextPage = currentPage + 1;
      fetchRegistrations(nextPage, searchTerm);
    }
  }, [loading, pageChanging, currentPage, totalHits, fetchRegistrations, searchTerm]);

  const handlePrevPage = useCallback(() => {
    if (!loading && !pageChanging && currentPage > 1) {
      const prevPage = currentPage - 1;
      fetchRegistrations(prevPage, searchTerm);
    }
  }, [loading, pageChanging, currentPage, fetchRegistrations, searchTerm]);

  // Add effect to scroll to top on page change
  useEffect(() => {
    if (contentTopRef.current) {
      contentTopRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [currentPage]);

  const handleClientProfile = useCallback((clientId: string) => {
    navigate(`/dashboard/clients/${clientId}/history`);
  }, [navigate]);

  // Group registrations by day
  const groupedRegistrations = useMemo<DailyRegistrations[]>(() => {
    if (!registrations.length) return [];

    const groups: Record<string, { registrations: ClientRegistration[], representativeDate: Date }> = {};

    registrations.forEach(registration => {
      // Usamos la función auxiliar externa
      const dateKey = formatLocalDateKey(registration.createdAt);
      if (!groups[dateKey]) {
        groups[dateKey] = {
          registrations: [],
          representativeDate: registration.createdAt
        };
      }
      groups[dateKey].registrations.push(registration);
    });

    return Object.entries(groups)
      .map(([, groupData]) => ({
        // Usamos la función auxiliar externa
        date: formatDateDisplay(groupData.representativeDate),
        dateObj: groupData.representativeDate,
        registrations: groupData.registrations,
        count: groupData.registrations.length
      }))
      .sort((a, b) => b.dateObj.getTime() - a.dateObj.getTime());
  }, [registrations]);

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage * ITEMS_PER_PAGE < totalHits;

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        {/* Reference div at the top of the content */}
        <div ref={contentTopRef} id="content-top"></div>
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <UserPlus className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-accent" />
              Últimos Registros
            </h1>
            <p className="mt-1 text-sm text-content-2">
              Visualiza los clientes más recientes registrados en el sistema
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="px-4 py-5 sm:p-6">
            {/* Search Bar */}
            <div className="mb-4">
              <SearchInput
                onSearch={handleSearch}
                placeholder="Buscar por nombre o teléfono"
                className="max-w-lg"
              />
            </div>

            {/* Desktop View - Grouped by days */}
            <div className="hidden md:block">
              {loading && registrations.length === 0 ? (
                // Skeleton loading
                <div className="animate-pulse space-y-6">
                  {Array.from({ length: 2 }).map((_, groupIndex) => (
                    <div key={`skeleton-group-${groupIndex}`}>
                      <div className="h-6 bg-skeleton rounded w-40 mb-4"></div>
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-line">
                          <thead className="bg-surface-2">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                                Cliente
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                                Detalles
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                                Hora de Registro
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-surface divide-y divide-line">
                            {Array.from({ length: 3 }).map((_, rowIndex) => (
                              <tr key={`skeleton-row-${groupIndex}-${rowIndex}`}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="h-4 bg-skeleton rounded w-3/4 mb-2"></div>
                                  <div className="h-3 bg-skeleton rounded w-1/2"></div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="h-4 bg-skeleton rounded w-24 mb-2"></div>
                                  <div className="h-3 bg-skeleton rounded w-16"></div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="h-4 bg-skeleton rounded w-36"></div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              ) : !loading && registrations.length === 0 ? (
                <div className="text-center py-10 text-sm text-content-3">
                  {searchTerm
                    ? `No se encontraron resultados para la búsqueda: "${searchTerm}"`
                    : 'No hay registros recientes para mostrar.'}
                </div>
              ) : (
                <div className="space-y-8">
                  {groupedRegistrations.map((group, groupIndex) => (
                    <div key={`group-${groupIndex}`} className="animate-fade-in-up" style={{ animationDelay: `${0.2 + groupIndex * 0.1}s` }}>
                      {/* Date header with count */}
                      <div className="flex items-center mb-3">
                        <h3 className="text-lg font-medium text-content">
                          {group.date}
                        </h3>
                        <div className="ml-3 px-2 py-1 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full flex items-center">
                          <Users className="h-3 w-3 mr-1" />
                          {group.count} cliente{group.count !== 1 ? 's' : ''}
                        </div>
                      </div>

                      {/* Registrations table for this day */}
                      <div className="overflow-x-auto border rounded-lg">
                        <table className="min-w-full divide-y divide-line">
                          <thead className="bg-surface-2">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                                Cliente
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                                Detalles
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-content-3 uppercase tracking-wider">
                                Hora de Registro
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-surface divide-y divide-line">
                            {group.registrations.map((registration, _index) => (
                              <tr key={registration.id} className="hover:bg-surface-2 transition-all duration-150">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <ClientContactInfo
                                    clientId={registration.id}
                                    clientName={registration.visitorName}
                                    phoneNumber={registration.phoneNumber}
                                    countryCode={registration.countryCode}
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex flex-col space-y-2">
                                    {registration.gender && (
                                      <div className="flex items-center text-sm text-content-3">
                                        <Calendar className="h-4 w-4 mr-1.5 text-content-3" />
                                        <span>
                                          {registration.gender === 'male'
                                            ? 'Masculino'
                                            : registration.gender === 'female'
                                              ? 'Femenino'
                                              : 'Otro'}
                                        </span>
                                      </div>
                                    )}
                                    {hasMultipleBranches && registration.registrationBranchName && (
                                      <div className="flex items-center text-sm text-content-3">
                                        <Building className="h-4 w-4 mr-1.5 text-content-3" />
                                        <span>{registration.registrationBranchName}</span>
                                      </div>
                                    )}
                                    {registration.email && (
                                      <div className="text-sm text-content-3 truncate" title={registration.email}>
                                        {registration.email}
                                      </div>
                                    )}
                                    
                                    {/* ✅ MOSTRAR SALDO/PUNTOS INICIALES DINÁMICAMENTE */}
                                    {registration.points > 0 && (
                                      <div className="flex items-center text-sm font-medium">
                                        {loyaltyMode === 'cashback' ? (
                                            <>
                                                <DollarSign className="h-4 w-4 mr-1 text-green-600 dark:text-green-400" />
                                                <span className="text-green-700 dark:text-green-300">Saldo inicial: ${registration.points.toFixed(2)}</span>
                                            </>
                                        ) : (
                                            <>
                                                <Star className="h-4 w-4 mr-1 text-yellow-500" />
                                                <span className="text-content-2">Puntos iniciales: {Math.floor(registration.points)}</span>
                                            </>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center text-sm text-content-3">
                                    <CalendarClock className="h-4 w-4 mr-1.5 text-content-3" />
                                    <DateTimeDisplay
                                      date={registration.createdAt}
                                      format="timeOnly"
                                      useBusinessTZ={true}
                                      customTimeZone={timeZoneInfo.business.zone}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile View - Grouped by days */}
            <div className="md:hidden">
              {loading && registrations.length === 0 ? (
                // Skeleton for mobile cards
                <div className="animate-pulse space-y-6">
                  {Array.from({ length: 2 }).map((_, groupIndex) => (
                    <div key={`skeleton-mobile-group-${groupIndex}`}>
                      <div className="h-5 bg-skeleton rounded w-40 mb-3"></div>
                      <div className="space-y-3">
                        {Array.from({ length: 2 }).map((_, cardIndex) => (
                          <div key={`skeleton-mobile-card-${groupIndex}-${cardIndex}`} className="border rounded-lg p-3">
                            <div className="flex justify-between">
                              <div className="w-3/4">
                                <div className="h-4 bg-skeleton rounded w-2/3 mb-2"></div>
                                <div className="h-3 bg-skeleton rounded w-1/2 mb-2"></div>
                              </div>
                              <div className="h-5 w-12 bg-skeleton rounded"></div>
                            </div>
                            <div className="mt-2 pt-2 border-t border-line">
                              <div className="h-3 bg-skeleton rounded w-24"></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : !loading && registrations.length === 0 ? (
                <div className="text-center py-8 text-sm text-content-3">
                  {searchTerm
                    ? `No se encontraron resultados para la búsqueda: "${searchTerm}"`
                    : 'No hay registros recientes para mostrar.'}
                </div>
              ) : (
                <div className="space-y-6">
                  {groupedRegistrations.map((group, groupIndex) => (
                    <div key={`mobile-group-${groupIndex}`} className="animate-fade-in-up" style={{animationDelay: `${0.2 + groupIndex * 0.1}s`}}>
                      {/* Date header with count */}
                      <div className="flex items-center mb-2">
                        <h3 className="text-base font-medium text-content">
                          {group.date}
                        </h3>
                        <div className="ml-2 px-2 py-0.5 bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 text-xs font-medium rounded-full flex items-center">
                          <Users className="h-3 w-3 mr-1" />
                          {group.count}
                        </div>
                      </div>

                      {/* Registration cards for this day */}
                      <div className="space-y-3">
                        {group.registrations.map((registration) => (
                          <div
                            key={registration.id}
                            className="border rounded-lg p-3 hover:bg-surface-2 transition-all duration-150"
                            onClick={() => handleClientProfile(registration.id)}
                          >
                            <div className="flex justify-between items-start gap-2">
                              {/* Left side: Name and Contact */}
                              <div className="flex-grow">
                                <ClientContactInfo
                                  clientId={registration.id}
                                  clientName={registration.visitorName}
                                  phoneNumber={registration.phoneNumber}
                                  countryCode={registration.countryCode}
                                  iconSize="sm"
                                />
                                {hasMultipleBranches && registration.registrationBranchName && (
                                  <div className="text-xs text-content-3 mt-1 flex items-center">
                                    <Building className="h-3 w-3 mr-1 text-content-3" />
                                    {registration.registrationBranchName}
                                  </div>
                                )}
                                {registration.email && (
                                  <div className="text-xs text-content-3 mt-1 truncate">
                                    {registration.email}
                                  </div>
                                )}
                              </div>
                              {registration.gender && (
                                <div className="flex items-center text-xs text-content-3">
                                  <Calendar className="h-3 w-3 mr-1 text-content-3" />
                                  <span>
                                    {registration.gender === 'male'
                                      ? 'M'
                                      : registration.gender === 'female'
                                        ? 'F'
                                        : 'O'}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="mt-2 pt-2 border-t border-line flex justify-between items-center text-xs text-content-3">
                              <div className="flex items-center">
                                <CalendarClock className="h-3 w-3 mr-1 text-content-3"/>
                                <span>
                                  <DateTimeDisplay
                                    date={registration.createdAt}
                                    format="timeOnly"
                                    compact={true}
                                    useBusinessTZ={true}
                                    customTimeZone={timeZoneInfo.business.zone}
                                  />
                                </span>
                              </div>
                              
                              {/* ✅ MOSTRAR SALDO/PUNTOS INICIALES (MÓVIL) */}
                              {registration.points > 0 && (
                                <div>
                                    {loyaltyMode === 'cashback' ? (
                                        <span className="text-green-700 dark:text-green-300 font-medium flex items-center">
                                            <DollarSign className="h-3 w-3 mr-0.5" />
                                            {registration.points.toFixed(2)}
                                        </span>
                                    ) : (
                                        <span className="text-content-2 flex items-center">
                                            <Star className="h-3 w-3 mr-0.5 text-yellow-500" />
                                            {Math.floor(registration.points)} pts
                                        </span>
                                    )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* PaginationControls component */}
            {totalHits > ITEMS_PER_PAGE && (
              <PaginationControls
                currentPage={currentPage}
                isLoading={loading || pageChanging}
                onPrevPage={handlePrevPage}
                onNextPage={handleNextPage}
                canGoPrev={canGoPrev && !pageChanging}
                canGoNext={canGoNext && !pageChanging}
                itemsPerPage={ITEMS_PER_PAGE}
                countOnPage={registrations.length}
                totalItems={totalHits}
              />
            )}
          </div>
        </div>

        {/* Indicador de carga para cambio de página */}
        {pageChanging && (
          <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in z-50">
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
            <span className="text-sm font-medium text-content-2">Cambiando página...</span>
          </div>
        )}

        {/* Indicador de carga principal */}
        {loading && registrations.length > 0 && (
          <div className="fixed bottom-4 right-4 bg-surface rounded-full shadow-lg p-3 flex items-center animate-fade-in">
            <Loader2 className="h-5 w-5 text-blue-500 animate-spin mr-2" />
            <span className="text-sm font-medium text-content-2">Cargando...</span>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default LatestRegistrationsPage;